<template>
  <div class="catalogue-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <h1 class="hero-title">Features</h1>
        <p class="hero-subtitle">
          Discover all the powerful features that make eClub the complete club management solution for schools
        </p>
      </div>
    </section>

    <!-- Feature Categories -->
    <section class="catalogue-section">
      <div class="container">

        <!-- Club Management -->
        <div class="category">
          <h2 class="category-title">Club Management</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-plus"></i>
              </div>
              <h3>Club Creation</h3>
              <p>Create clubs with custom names, types, introductions, and avatars. Choose from 18+ club categories.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-search"></i>
              </div>
              <h3>Club Discovery</h3>
              <p>Browse and search clubs by name, description, or type. Discover clubs within your school network.</p>
            </div>
          </div>
        </div>

        <!-- Club Dashboard Modules -->
        <div class="category">
          <h2 class="category-title">Club Dashboard Modules</h2>
          <p class="category-description">Each club comes with a comprehensive dashboard featuring the following modules:</p>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-postcard"></i>
              </div>
              <h3>Profile</h3>
              <p>Edit club name, introduction, and avatar. Display school affiliation and club type tags.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-data-analysis"></i>
              </div>
              <h3>Statistics</h3>
              <p>View member counts, activity statistics, and recognition summaries at a glance.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-user"></i>
              </div>
              <h3>Club Members</h3>
              <p>Add students individually or via CSV import. Track member roles, grades, and manage retirements.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-star-on"></i>
              </div>
              <h3>Leadership</h3>
              <p>Define positions like President and Secretary. Assign members and track tenure periods by class year.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-s-custom"></i>
              </div>
              <h3>Advisers</h3>
              <p>View assigned advisers with contact information including name, email, and phone number.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-date"></i>
              </div>
              <h3>Events</h3>
              <p>Schedule club events with date, time, and location. Track upcoming and completed events.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-chat-dot-round"></i>
              </div>
              <h3>Posts</h3>
              <p>Create announcements and discussions. Enable or lock comments, and manage post visibility.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-finished"></i>
              </div>
              <h3>Todos</h3>
              <p>Create and assign tasks to members. Set due dates, add comments, and track completion status.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-folder"></i>
              </div>
              <h3>Documents</h3>
              <p>Upload files up to 10MB each with 1GB total storage. Support for PDF, Office, images, and archives.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-trophy"></i>
              </div>
              <h3>Internal Achievements</h3>
              <p>Record club achievements with dates and categories. Track awards, competitions, and recognitions.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-medal"></i>
              </div>
              <h3>External Honors</h3>
              <p>Apply for verified external honors with admin approval. Attach events, posts, and documents as evidence.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-setting"></i>
              </div>
              <h3>Settings</h3>
              <p>Configure club status (active/inactive/archived), visibility settings, and join approval requirements.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-tickets"></i>
              </div>
              <h3>Content Orders</h3>
              <p>View and manage content orders for the club. Track order quantities, totals, and distribution status.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-s-claim"></i>
              </div>
              <h3>Membership</h3>
              <p>Students can view their membership status, join date, and role within the club.</p>
            </div>
          </div>
        </div>

        <!-- Content & Learning -->
        <div class="category">
          <h2 class="category-title">Content & Learning</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-reading"></i>
              </div>
              <h3>Content Discovery</h3>
              <p>Browse educational decks and bundles. Filter by type and view creator information.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-goods"></i>
              </div>
              <h3>Content Profiles</h3>
              <p>View detailed content information including groups, stacks, items, and pricing.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-share"></i>
              </div>
              <h3>Content Distribution</h3>
              <p>Share content to students. Purchase copies and track distribution status.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-notebook-2"></i>
              </div>
              <h3>My Contents</h3>
              <p>Access purchased and assigned content. Track completion status and received dates.</p>
            </div>
          </div>
        </div>

        <!-- Payment & Orders -->
        <div class="category">
          <h2 class="category-title">Payment & Orders</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-bank-card"></i>
              </div>
              <h3>Secure Payments</h3>
              <p>Process payments via credit card (Stripe) or bank transfer with invoice support.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-tickets"></i>
              </div>
              <h3>Order Management</h3>
              <p>Track order history, quantities, and totals. Filter orders by club and content.</p>
            </div>
          </div>
        </div>

        <!-- User Roles -->
        <div class="category">
          <h2 class="category-title">User Roles</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-user"></i>
              </div>
              <h3>Students</h3>
              <p>Join clubs, submit applications, participate in activities, and access learning content.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-s-check"></i>
              </div>
              <h3>Advisers</h3>
              <p>Create and manage clubs, generate registration links, discover clubs, and distribute content.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-office-building"></i>
              </div>
              <h3>School Admins</h3>
              <p>Oversee all school clubs, manage adviser assignments, and handle account forms.</p>
            </div>
          </div>
        </div>

        <!-- Authentication & Security -->
        <div class="category">
          <h2 class="category-title">Authentication & Security</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-lock"></i>
              </div>
              <h3>Secure Login</h3>
              <p>Multi-type authentication for students, advisers, and admins with email verification.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">
                <i class="el-icon-edit-outline"></i>
              </div>
              <h3>Registration</h3>
              <p>Easy registration for students and advisers with customizable organization details.</p>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>Ready to Transform Your Club Management?</h2>
        <p>Join schools worldwide using eClub to streamline their extracurricular activities.</p>
        <router-link to="/official/contact" class="btn btn-white">Get Started</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'OfficialCatalogue'
}
</script>

<style scoped lang="scss">
.catalogue-page {
  .hero {
    background: linear-gradient(135deg, #fff5f3 0%, #ffe6e3 100%);
    padding: 80px 0;
    text-align: center;

    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .hero-title {
      font-size: 3rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 20px;
    }

    .hero-subtitle {
      font-size: 1.25rem;
      color: #666;
      line-height: 1.6;
    }
  }

  .catalogue-section {
    padding: 60px 0;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }
  }

  .category {
    margin-bottom: 60px;

    &:last-child {
      margin-bottom: 0;
    }

    .category-title {
      font-size: 1.8rem;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 15px;
      padding-bottom: 15px;
      border-bottom: 2px solid #FF6B4A;
      display: inline-block;
    }

    .category-description {
      color: #666;
      font-size: 1rem;
      margin-bottom: 25px;
      line-height: 1.6;
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 25px;
  }

  .feature-card {
    background: #fff;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }

    .feature-icon {
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, #FF6B4A 0%, #ff8a6b 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;

      i {
        font-size: 24px;
        color: #fff;
      }
    }

    h3 {
      font-size: 1.2rem;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 12px;
    }

    p {
      font-size: 0.95rem;
      color: #666;
      line-height: 1.6;
      margin: 0;
    }
  }

  .cta {
    background: linear-gradient(135deg, #FF6B4A 0%, #ff8a6b 100%);
    padding: 80px 0;
    text-align: center;

    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 0 20px;
    }

    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #fff;
      margin-bottom: 20px;
    }

    p {
      font-size: 1.2rem;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 30px;
    }

    .btn-white {
      display: inline-block;
      padding: 15px 40px;
      background: #fff;
      color: #FF6B4A;
      border-radius: 25px;
      text-decoration: none;
      font-weight: 600;
      font-size: 1.1rem;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
      }
    }
  }
}

@media (max-width: 768px) {
  .catalogue-page {
    .hero {
      padding: 60px 0;

      .hero-title {
        font-size: 2.2rem;
      }

      .hero-subtitle {
        font-size: 1.1rem;
      }
    }

    .catalogue-section {
      padding: 40px 0;
    }

    .category {
      margin-bottom: 40px;

      .category-title {
        font-size: 1.5rem;
      }
    }

    .features-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .feature-card {
      padding: 25px;
    }

    .cta {
      padding: 60px 0;

      h2 {
        font-size: 1.8rem;
      }

      p {
        font-size: 1rem;
      }
    }
  }
}
</style>
